/**
 * src/features/auth/ui/SessionRevokedOverlay.tsx
 *
 * Phase 43 (PWD-06) — the notice a tab sees when useAuth's cross-tab listener learns (via a
 * foreign-origin `session-revoked` broadcast on the shared `estimatix-auth` BroadcastChannel)
 * that its session has already been killed server-side by a password reset or change made
 * elsewhere. Mounted globally in App.tsx, outside RequireAuth, so it can interrupt whichever
 * route the tab happens to have open — most importantly the editor, where unsaved edits are
 * most likely to be accumulating against a session that is already dead (T-43-18).
 *
 * Deliberately NOT built on the shared responsive-modal primitive (`src/shared/ui/
 * responsive-modal.tsx` and its layout helper) or on Radix Dialog directly: that layout
 * helper only forwards its `dismissible` prop to the mobile Drawer branch, never to the
 * desktop Dialog branch (lines 56-67 of the layout file) — so a surface that must never
 * close on Escape or an outside click cannot be expressed through that component. This is
 * a bare `fixed` layer with no Radix Dialog semantics to fight or suppress.
 *
 * Exactly one interactive element (the call-to-action). With only one focusable node in this
 * layer, Tab containment is complete by construction — no focus-trap dependency is added
 * (T-43-SC: this plan installs zero packages).
 */

import { FC, KeyboardEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import Button from '@/shared/ui/button';
import { useAuthStore } from '@/stores/useAuthStore';

export const SessionRevokedOverlay: FC = () => {
  const { t } = useTranslation();
  const sessionRevoked = useAuthStore((s) => s.sessionRevoked);
  const ctaRef = useRef<HTMLButtonElement>(null);

  // Move focus to the single call-to-action the instant the notice appears — it must not
  // silently sit behind whatever element already had focus in the interrupted route.
  useEffect(() => {
    if (sessionRevoked) {
      ctaRef.current?.focus();
    }
  }, [sessionRevoked]);

  if (!sessionRevoked) return null;

  // Containment by construction: exactly one focusable element exists in this layer, so any
  // Tab press is refocused onto it rather than escaping to background content.
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      ctaRef.current?.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-red-50 dark:bg-red-950/40 flex items-center justify-center px-6"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-revoked-overlay-title"
      aria-describedby="session-revoked-overlay-body"
      onKeyDown={handleKeyDown}
    >
      <div className="max-w-sm text-center flex flex-col items-center gap-4">
        <LogOut size={32} className="text-red-600 dark:text-red-400" aria-hidden="true" />
        <h2
          id="session-revoked-overlay-title"
          className="text-lg font-bold text-red-900 dark:text-red-500"
        >
          {t('auth.session_revoked.title', "You've been signed out")}
        </h2>
        <p id="session-revoked-overlay-body" className="text-body text-red-800 dark:text-red-300">
          {t(
            'auth.session_revoked.body',
            'Your password was changed in another tab or device. Sign in again to continue.',
          )}
        </p>
        {/* Full page load, never client-side navigate: every piece of in-memory state
            belonging to the dead session (Zustand, React) must be discarded, not carried
            into a freshly mounted sign-in screen — that is the point of PWD-06 (T-43-18). */}
        <Button ref={ctaRef} onClick={() => window.location.assign('/')}>
          {t('auth.session_revoked.cta', 'Sign in again')}
        </Button>
      </div>
    </div>
  );
};
